import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { inferEnvironment, useLocalStorage } from '@amzn/et-console-components';
import ApiClient from '../ApiClient';
import TranslatorJobsView from './TranslatorJobsView';
import { logGroup } from '../utils/logUtils';
import initialMetricsPublisher from '../utils/metricsPublisher';
import { publishCountForMethod } from '@amzn/ts-ui-metrics';
import { OfferUtils } from '../utils/offerUtils';
import { isOfferAllowedToBundle } from '../utils/bundlingAllowlist';
import {
  publishBundleClaimOutcome,
  publishBundlingExpansionState,
  publishBundlingShown,
} from '../utils/metrics';
import moment from 'moment';
import { PROD_ENV } from '@amzn/et-console-components/dist/utils/inferEnvironment';

const validationError = new Error();
validationError.status = 520;
validationError.details = {};
validationError.details.errors = [{ message: 'Unexpected response from server' }];

const fetchStats = () => {
  const url = `/me/stats`;

  return ApiClient.httpGet(url).then(resp => {
    return resp;
  });
};

const fetchOffers = (offerStatus, page = 1, pageSize = 50) => {
  // Backend uses 0-based page numbers, frontend uses 1-based
  const backendPage = page - 1;
  const url = `/v2/me/offer/?status=${offerStatus}&pageNum=${backendPage}&pageSize=${pageSize}`;

  return ApiClient.httpGet(url)
    .then(response => {
      // Validate v2 API paginated response format
      if (
        !response ||
        !response.offers ||
        response.page === undefined ||
        response.totalPages === undefined ||
        response.offers.constructor !== Array
      ) {
        throw validationError;
      }

      const offers = response.offers;
      const pagination = {
        currentPage: response.page + 1, // Backend uses 0-based, frontend uses 1-based
        totalPages: response.totalPages,
        pageSize: pageSize,
      };

      // Emit UX metrics for the offer count displayed to linguist
      if (offerStatus === 'EMAILED' && offers.length !== 0) {
        let metricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(
          'OffersAvailableToLinguist'
        );
        // all offer items in the list have same userId
        let user = offers[0].freelancerUri;
        metricsPublisher.publishString('User', user);
        metricsPublisher.publishString('OffersCount', offers.length);
      }

      // Resolved per-linguist in ATMSAssignment. Coerced rather than passed through so an
      // upstream that omits it (any deploy before the flag shipped) reads as off.
      const offerBundlingExpansion = response.offerBundlingExpansion === true;

      // Once per offers-view fetch, never per render. The value is the same on both screens, so
      // emitting for one of them keeps the treated population from being double-counted.
      if (offerStatus === 'EMAILED') {
        publishBundlingExpansionState(response.offerBundlingExpansion);
      }

      return { offers, pagination, offerBundlingExpansion };
    })
    .catch(error => {
      // Add specific error handling for pagination-related failures
      if (error.status === 400 && error.details && error.details.errors) {
        const paginationErrors = error.details.errors.filter(
          e =>
            e.message &&
            (e.message.includes('page') ||
              e.message.includes('pageSize') ||
              e.message.includes('pagination'))
        );
        if (paginationErrors.length > 0) {
          const paginationError = new Error('Invalid pagination parameters');
          paginationError.status = 400;
          paginationError.details = { errors: paginationErrors };
          throw paginationError;
        }
      }
      throw error;
    });
};

const getBundleHeaders = bundle =>
  bundle && {
    'X-Offer-Bundle-Id': bundle.id,
    'X-Offer-Bundle-Size': String(OfferUtils.getFileCount(bundle)),
  };

export const updateOfferStatus = (job, status, bundle) => {
  // emit UX metrics: offer is accepted by linguist
  if (status === 'ACCEPTED') {
    let metricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(
      'OfferAcceptedByLinguist'
    );
    metricsPublisher.publishString('OfferId', job.id);
    metricsPublisher.publishString('AcceptedBy', job.freelancerUri);
    metricsPublisher.publishString('JobUri', job.jobUri);
  }
  return ApiClient.httpPut(
    '/me/offer/status',
    {
      jobUri: job.jobUri,
      status: status,
      offerId: job.id,
      freelancerUri: job.freelancerUri,
    },
    getBundleHeaders(bundle)
  );
};

const getErrorMessage = errorDetails => {
  const name = errorDetails['name'];
  const message = errorDetails['message'];

  if (name === 'JobSegmentsUnconfirmed') {
    return 'Job is not finished. Please confirm all segments prior to completing job.';
  }

  if (name === 'JobNotDownloadable') {
    return 'There is a problem preventing the creation of the translated file, please check for QA errors in TWE and try again.';
  }

  return name + ': ' + message;
};

const publishStatsMetrics = (actionMetricsPublisher, stats) => {
  actionMetricsPublisher.publishCounter('AcceptedOffersCount', stats.acceptedOffersCount);
  actionMetricsPublisher.publishCounter('MaxAcceptableOffers', stats.maxAcceptableOffers);
  actionMetricsPublisher.publishCounter(
    'AcceptedWeightedWorkUnits',
    stats.acceptedWeightedWorkUnits
  );
  actionMetricsPublisher.publishCounter(
    'MaxAcceptedWeightedWorkUnits',
    stats.maxAcceptedWeightedWorkUnits
  );
};

// A bundle has no jobUri of its own. Like its parentOfferId (OfferUtils.calculateBundleValues),
// its post mortem scoring parent job is resolved from its first offer.
const getPostMortemScoringJobUri = offer =>
  offer.isBundle ? Object.values(offer.childOffers)[0].jobUri : offer.jobUri;

const bundleOffers = (offers, offerBundlingExpansion) => {
  // divide array into offers that are eligible/ineligible for bundling
  const [eligibleOffers, retOffers] = offers.reduce(
    ([eligibleOffers, retOffers], offer) => {
      return offer.workUnits < 500 &&
        isOfferAllowedToBundle(offer, offerBundlingExpansion) &&
        // I'm making this change in non-prod envs to fix the functional tests; I'm working with @afsin
        // to extend the change to prod as well
        (!OfferUtils.isExclusiveOffer(offer) || inferEnvironment() !== PROD_ENV)
        ? [[...eligibleOffers, offer], retOffers]
        : [eligibleOffers, [...retOffers, offer]];
    },
    [[], []]
  );

  // group offers by Client|DueDate(Date Only)|Skill|SourceLocale|TargetLocale|Subject|MediaType.
  // Subject and media type are in the key so that expanding the allowlist beyond a single pair
  // cannot put offers of different pairs in one bundle -- the bundle takes its subject and media
  // type from its first offer, so a mixed group would mislabel itself.
  const groupedOffers = eligibleOffers
    .map(offer => {
      offer.key = `${offer.clientUri || '-'}|${moment(offer.dateDue).format('MM/DD/YYYY')}|${
        offer.translationSkill
      }|${offer.sourceLocale}|${offer.targetLocale}|${offer.subject}|${offer.mediaType}`;
      return offer;
    })
    .reduce((groups, offer) => {
      (groups[offer.key] = groups[offer.key] || []).push(offer);
      return groups;
    }, {});

  // if more than one offer in group bundle the offers, add single or bundle to retOffers
  Object.values(groupedOffers).forEach(group => {
    retOffers.push(group.length > 1 ? OfferUtils.createBundle(group) : group[0]);
  });

  const actionMetricsPublisher = publishCountForMethod(initialMetricsPublisher, 'bundleOffers');

  let numBundlesCreated = 0;
  let numOffersUnbundled = 0;
  let numOffersBundled = 0;
  retOffers.forEach(offer => {
    if (!offer.isBundle) {
      numOffersUnbundled++;
    } else {
      numBundlesCreated++;
      const bundleSize = Object.keys(offer.childOffers).length;
      numOffersBundled += bundleSize;
      actionMetricsPublisher.publishCounter('CreatedBundleSize', bundleSize);
    }
  });
  actionMetricsPublisher.publishCounter('NumBundlesCreated', numBundlesCreated);
  actionMetricsPublisher.publishCounter('NumOffersUnbundled', numOffersUnbundled);
  publishBundlingShown(
    offerBundlingExpansion,
    numOffersBundled,
    numOffersUnbundled,
    numBundlesCreated
  );

  // Wave 0 baseline: bundling effectiveness sliced by subject + media type.
  // Observe-only. Emitted in addition to the aggregate metrics above so existing
  // dashboards are unaffected. A bundle's offers share one subject + media-type
  // pair (its subject/mediaType come from its first offer), so the per-pair slice
  // is well-defined. "offersShownAsBundled" / "totalOffersShown" per pair let a
  // per-pair bundling rate be derived to compare before vs after the expansion.
  const effectivenessByPair = {};
  retOffers.forEach(offer => {
    const pairKey = `${offer.subject}|${offer.mediaType}`;
    const pairStats =
      effectivenessByPair[pairKey] ||
      (effectivenessByPair[pairKey] = {
        subject: offer.subject,
        mediaType: offer.mediaType,
        offersShownAsBundled: 0,
        totalOffersShown: 0,
      });
    if (offer.isBundle) {
      const bundleSize = Object.keys(offer.childOffers).length;
      pairStats.offersShownAsBundled += bundleSize;
      pairStats.totalOffersShown += bundleSize;
    } else {
      pairStats.totalOffersShown += 1;
    }
  });

  Object.values(effectivenessByPair).forEach(pairStats => {
    const pairMetricsPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(
      'BundlingEffectiveness'
    );
    pairMetricsPublisher.publishString('Subject', pairStats.subject);
    pairMetricsPublisher.publishString('MediaType', pairStats.mediaType);
    pairMetricsPublisher.publishCounter('OffersShownAsBundled', pairStats.offersShownAsBundled);
    pairMetricsPublisher.publishCounter('TotalOffersShown', pairStats.totalOffersShown);
  });

  return retOffers;
};

const TranslatorJobs = ({ forReservations, features, globalState, history, location }) => {
  const [jobs, setJobs] = useState([]);
  const [segmentMetadataByJob, setSegmentMetadataByJob] = useState({});
  const [postMortemScoringParentOfferMetadata, setPostMortemScoringParentOfferMetadata] = useState(
    {}
  );
  const [postMortemScoringParentJobMetadata, setPostMortemScoringParentJobMetadata] = useState({});
  const [assignedJobsCount, setAssignedJobsCount] = useState(0);
  const [maxAssignedJobsCount, setMaxAssignedJobsCount] = useState(0);
  const [acceptedWeightedWorkUnits, setAcceptedWeightedWorkUnits] = useState(0);
  const [maxAcceptedWeightedWorkUnits, setMaxAcceptedWeightedWorkUnits] = useState(0);
  const [isFetching, setIsFetching] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [inflightStatusUpdates, setInflightStatusUpdates] = useState(new Map());
  const [alerts, setAlerts] = useState([]);
  const [timeNow, setTimeNow] = useState(Date.now());
  const [isUpdatingOfferMap, setIsUpdatingOfferMap] = useState(new Map());
  const [isUpdatingOfferForClaim, setIsUpdatingOfferForClaim] = useState(false);
  const [offerBundlingExpansion, setOfferBundlingExpansion] = useState(false);
  const [ackedStyleguideMove, setAckedStyleguideMove] = useLocalStorage(
    'ackedStyleguideMove',
    false
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(50);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  // Parse page number from URL
  const getPageFromURL = () => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    // Validate page parameter
    if (!pageParam) {
      return 1; // Default to page 1 if no parameter
    }

    const pageNumber = parseInt(pageParam, 10);

    // Check if it's a valid number
    if (isNaN(pageNumber) || pageNumber < 1) {
      return null; // Invalid page number, will trigger redirect
    }

    return pageNumber;
  };

  // Update URL with page number
  const updateURL = page => {
    const params = new URLSearchParams(location.search);

    if (page === 1) {
      // Remove page parameter for page 1 (cleaner URLs)
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }

    const newSearch = params.toString();
    const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

    // Only update if the URL actually changed
    if (newPath !== `${location.pathname}${location.search}`) {
      history.push(newPath);
    }
  };

  useEffect(() => {
    if (!ackedStyleguideMove) {
      publishCountForMethod(initialMetricsPublisher, 'StyleguideMovedAlertShown');

      setAlerts(prev => [
        ...prev,
        {
          header: 'Style guides have moved!',
          content:
            'Style guides are now available in the ATMS Web Editor. Find them in the References tab of the bottom panel.',
          type: 'warning',
          onDismiss: () => {
            publishCountForMethod(initialMetricsPublisher, 'StyleguideMovedAlertDismissed');

            setAckedStyleguideMove(true);
          },
        },
      ]);
    }
  }, []);

  const jobStatus = forReservations ? 'EMAILED' : 'ASSIGNED';

  const fetchStatsAndOffers = (page = 1, pageSizeParam = 50) => {
    // reset state
    setIsFetching(true);
    setIsPaginationLoading(true);
    setInflightStatusUpdates(new Map());
    setIsUpdatingOfferMap(new Map());
    const actionMetricsPublisher = publishCountForMethod(
      initialMetricsPublisher,
      'FetchStatsAndOffers'
    );
    return Promise.all([fetchOffers(jobStatus, page, pageSizeParam), fetchStats()])
      .then(([{ offers, pagination, offerBundlingExpansion }, stats]) => {
        actionMetricsPublisher.publishCounter('SuccessCount', 1);
        actionMetricsPublisher.publishCounter('OffersCount', offers.length);
        publishStatsMetrics(actionMetricsPublisher, stats);
        logGroup(`fetchOffers (${jobStatus})`, { offers, pagination, stats });
        // bundle offers if status == EMAILED
        if (jobStatus === 'EMAILED') {
          offers = bundleOffers(offers, offerBundlingExpansion);
        }
        // Bundling must read the flag from the local above, never from this state: a value set
        // here is not readable until the next render.
        setOfferBundlingExpansion(offerBundlingExpansion);
        setJobs(offers);
        setAssignedJobsCount(stats.acceptedOffersCount);
        setMaxAssignedJobsCount(stats.maxAcceptableOffers);
        setAcceptedWeightedWorkUnits(stats.acceptedWeightedWorkUnits);
        setMaxAcceptedWeightedWorkUnits(stats.maxAcceptedWeightedWorkUnits);

        // Update pagination state
        setCurrentPage(pagination.currentPage);
        setTotalPages(pagination.totalPages);

        setIsFetching(false);
        setIsPaginationLoading(false);
        setLoadError(false);

        // Edge case: Handle invalid page numbers after fetch
        // If the requested page now exceeds total pages, redirect to page 1
        if (page > pagination.totalPages && pagination.totalPages > 0) {
          updateURL(1);
          // Re-fetch page 1 to show correct data
          return fetchStatsAndOffers(1, pageSizeParam);
        }

        // Return pagination metadata for caller to use
        return { offers, pagination, stats };
      })
      .catch(error => {
        actionMetricsPublisher.publishCounter('FailureCount', 1);
        setIsFetching(false);
        setIsPaginationLoading(false);
        setLoadError(true);
        console.error(error);

        // Provide specific error messages for pagination failures
        let errorMessage = 'Failed to load jobs for translator: ' + error;
        if (error.message === 'Invalid pagination parameters') {
          errorMessage = 'Failed to load page';
        } else if (error.status >= 500) {
          errorMessage = 'Unable to load jobs';
        }

        setAlerts(prev => [...prev, { content: errorMessage, type: 'error' }]);

        // Edge case: On error, preserve current state for graceful degradation
        // Don't clear jobs or pagination state - keep showing what we had

        // Re-throw to allow caller to handle if needed
        throw error;
      });
  };

  const fetchSegmentMetadata = async jobPartIds => {
    if (jobPartIds.length === 0) {
      return {};
    }

    try {
      return await ApiClient.httpPost(`/me/getJobPartSegmentCounts`, { jobPartIds });
    } catch (e) {
      return jobPartIds.reduce((acc, id) => {
        return { ...acc, [id]: e.toString() };
      }, {});
    }
  };

  const fetchPostMortemScoringParentOfferMetadata = async parentToChildOfferIds => {
    const parentOfferIds = Object.keys(parentToChildOfferIds);
    if (parentOfferIds.length === 0) {
      console.info(
        'No parent offer ids found; skipping fetching postmortem scoring parent offer metadata'
      );
      return {};
    }

    try {
      const parentOfferMetadataResult = await ApiClient.httpPost(
        '/me/postMortemScoringParentOffer',
        { parentOfferIds }
      );
      return parentOfferMetadataResult['parentOfferMetadata'].reduce((acc, val) => {
        acc[parentToChildOfferIds[val['id']]] = val;
        return acc;
      }, {});
    } catch (e) {
      // TODO: Show error to user
      console.warn('Failed to load parent offer metadata');
    }
  };

  const fetchPostMortemScoringParentJobMetadata = async jobUrisToOfferIds => {
    const jobUris = Object.keys(jobUrisToOfferIds);
    if (jobUris.length === 0) {
      console.info('No job URIs found; skipping fetching postmortem scoring parent job metadata');
      return {};
    }

    try {
      const parentOfferMetadataResult = await ApiClient.httpPost('/me/postMortemScoringParentJob', {
        jobUris,
      });
      return parentOfferMetadataResult['parentJobMetadata'].reduce((acc, val) => {
        acc[jobUrisToOfferIds[val['jobUri']]] = val;
        return acc;
      }, {});
    } catch (e) {
      // TODO: Show error to user
      console.warn('Failed to load parent job metadata');
    }
  };

  const updateStatus = (job, status, updateStats = true, parentBundle) => {
    // don't allow any status updates if previous update has been initiated
    if (
      inflightStatusUpdates.get(job.id) &&
      !inflightStatusUpdates.get(job.id).error &&
      !inflightStatusUpdates.get(job.id).warning
    ) {
      return;
    }

    const actionMetricsPublisher = publishCountForMethod(
      initialMetricsPublisher,
      'UpdateOfferStatusAndGetStats'
    );
    actionMetricsPublisher.publishString(
      'OfferCount',
      !job.isBundle ? 1 : Object.values(job.childOffers).length
    );
    actionMetricsPublisher.publishString('TargetStatus', status);

    // Set the status update as being inflight
    setInflightStatusUpdates(prev => {
      prev.set(job.id, { status, complete: false, error: false, warning: false });
      return prev;
    });

    // Set the status update of offer for Complete All Button
    setIsUpdatingOfferMap(prev => {
      prev.set(job.id, true);
      return prev;
    });

    //set the status for Claim Button
    setIsUpdatingOfferForClaim(true);

    const fetchStatsIfNeeded = () => {
      if (updateStats) {
        return fetchStats();
      }
    };

    const handleFetchedStatsIfNeeded = stats => {
      if (updateStats) {
        actionMetricsPublisher.publishCounter('StatsSuccessCount', 1);
        publishStatsMetrics(actionMetricsPublisher, stats);
        setAssignedJobsCount(stats.acceptedOffersCount);
        setMaxAssignedJobsCount(stats.maxAcceptableOffers);
        setAcceptedWeightedWorkUnits(stats.acceptedWeightedWorkUnits);
        setMaxAcceptedWeightedWorkUnits(stats.maxAcceptedWeightedWorkUnits);
      }
    };

    if (job.isBundle) {
      const bundle = job;

      const childOffers = Object.values(bundle.childOffers);
      return (
        Promise.all(childOffers.map(childOffer => updateStatus(childOffer, status, false, bundle)))
          // update stats because they could have changed
          .then(fetchStatsIfNeeded)
          .then(handleFetchedStatsIfNeeded)
          .finally(() => {
            const bundleErrorMessages = childOffers.reduce((errorMessages, offer) => {
              if (inflightStatusUpdates.get(offer.id).error) {
                errorMessages.push(inflightStatusUpdates.get(offer.id).error);
              }
              return errorMessages;
            }, []);

            actionMetricsPublisher.publishCounter(
              'BundledOfferUpdateSuccessCount',
              childOffers.length - bundleErrorMessages.length
            );
            actionMetricsPublisher.publishCounter(
              'BundledOfferUpdateFailureCount',
              bundleErrorMessages.length
            );
            publishBundleClaimOutcome(
              childOffers.length - bundleErrorMessages.length,
              bundleErrorMessages.length
            );
            // Wave 0 baseline: slice the claim outcome (offers claimed via the bundle
            // vs failed) by the bundle's subject + media type. Observe-only; a bundle
            // shares one pair, so this per-pair claim slice is well-defined.
            actionMetricsPublisher.publishString('Subject', bundle.subject);
            actionMetricsPublisher.publishString('MediaType', bundle.mediaType);

            let bundleUpdateFinalState = 'Success';
            if (bundleErrorMessages.length === childOffers.length) {
              bundleUpdateFinalState = 'Failed';
            } else if (bundleErrorMessages.length > 0) {
              bundleUpdateFinalState = 'Partial';
            }
            actionMetricsPublisher.publishString('BundleUpdateFinalState', bundleUpdateFinalState);

            if (bundleErrorMessages.length > 0) {
              actionMetricsPublisher.publishStringTruncate(
                'BundleUpdateErrors',
                bundleErrorMessages.join(',')
              );
              if (bundleErrorMessages.length === childOffers.length) {
                setInflightStatusUpdates(prev => {
                  prev.get(bundle.id).error = 'Error occurred updating jobs';
                  return prev;
                });
              } else {
                setInflightStatusUpdates(prev => {
                  prev.get(
                    bundle.id
                  ).warning = `Error occurred updating jobs, ${childOffers.length -
                    bundleErrorMessages.length}/${childOffers.length} job(s) claimed successfully`;
                  return prev;
                });
              }
            }

            // mark update as complete for the bundle
            setInflightStatusUpdates(prev => {
              prev.get(job.id).complete = true;
              return prev;
            });

            // Set the status of offer
            setIsUpdatingOfferMap(prev => {
              prev.set(job.id, false);
              return prev;
            });

            //set the status for Claim Button
            setIsUpdatingOfferForClaim(false);
          })
      );
    } else {
      // update offer status
      return (
        updateOfferStatus(job, status, parentBundle)
          .then(() => {
            actionMetricsPublisher.publishCounter('UpdateSuccessCount', 1);
          })
          // update stats because they could have changed
          .then(fetchStatsIfNeeded)
          .then(handleFetchedStatsIfNeeded)
          .catch(error => {
            actionMetricsPublisher.publishCounter('FailureCount', 1);
            error.details['errors'].forEach(errorDetails => {
              setInflightStatusUpdates(prev => {
                prev.get(job.id).error = getErrorMessage(errorDetails);
                return prev;
              });
            });
          })
          .finally(() => {
            // mark update as complete
            setInflightStatusUpdates(prev => {
              prev.get(job.id).complete = true;
              return prev;
            });

            // Set the status of offer
            setIsUpdatingOfferMap(prev => {
              prev.set(job.id, false);
              return prev;
            });

            //set the status for Claim Button
            setIsUpdatingOfferForClaim(false);
          })
      );
    }
  };

  const updateStatusForAll = (jobs, status) => {
    // Track all update promises to know when bulk action completes
    const updatePromises = jobs.map(job => updateStatus(job, status));

    // After all updates complete, refresh the current page
    Promise.all(updatePromises)
      .then(() => {
        // Refresh the current page to show updated job statuses
        fetchStatsAndOffers(currentPage, pageSize)
          .then(({ pagination }) => {
            // Edge case: Handle empty pages after bulk actions
            if (pagination.totalJobs === 0) {
              // All jobs are gone, stay on page 1
              if (currentPage !== 1) {
                updateURL(1);
                fetchStatsAndOffers(1, pageSize);
              }
            } else if (currentPage > pagination.totalPages && pagination.totalPages > 0) {
              // Edge case: Current page exceeds total pages after bulk action
              updateURL(1);
              fetchStatsAndOffers(1, pageSize);
            }
          })
          .catch(error => {
            // Edge case: Graceful degradation for API failures
            // Error already handled in fetchStatsAndOffers with user-visible message
            console.error('Failed to refresh after bulk action:', error);
          });
      })
      .catch(error => {
        // Edge case: Handle bulk action failures gracefully
        console.error('Bulk action failed:', error);
        // Individual errors are already shown per job, no need for additional alert
      });
  };

  const handlePageChange = newPage => {
    let page = newPage;
    // Edge case: Validate page number
    if (newPage < 1) {
      page = 1;
      updateURL(1);
      return;
    }

    if (newPage > totalPages && totalPages > 0) {
      page = 1;
      updateURL(1);
      return;
    }

    // Update URL with new page number
    updateURL(page);

    // Set loading state for pagination transition
    setIsPaginationLoading(true);

    // Fetch new page data
    fetchStatsAndOffers(page, pageSize)
      .then(() => {
        // Scroll to top of page on successful load
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch(() => {
        // Error already handled in fetchStatsAndOffers
        // Graceful degradation: Keep showing current page on error
        setIsPaginationLoading(false);
      });
  };

  // Handle URL-based page navigation
  useEffect(() => {
    let urlPage = getPageFromURL();

    // Handle invalid page numbers - redirect to page 1
    if (urlPage === null) {
      urlPage = 1;
      updateURL(1);
      return;
    }

    // Fetch data for the page specified in URL
    fetchStatsAndOffers(urlPage, pageSize);

    const id = setInterval(() => setTimeNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Handle URL changes (browser back/forward)
  useEffect(() => {
    let urlPage = getPageFromURL();

    // Handle invalid page numbers - redirect to page 1
    if (urlPage === null) {
      urlPage = 1;
      updateURL(1);
      return;
    }

    // Only fetch if the URL page differs from current page
    if (urlPage !== currentPage && urlPage <= totalPages) {
      setIsPaginationLoading(true);
      fetchStatsAndOffers(urlPage, pageSize);
    } else if (urlPage > totalPages && totalPages > 0) {
      // Page number exceeds total pages - redirect to page 1
      updateURL(1);
    }
  }, [location.search]);

  useEffect(() => {
    fetchSegmentMetadata(jobs.map(j => j.jobPartId)).then(result =>
      setSegmentMetadataByJob(result)
    );
    fetchPostMortemScoringParentOfferMetadata(
      jobs
        .filter(j => j.isPostMortemScoring && j.parentOfferId)
        .reduce((acc, val) => {
          acc[val['parentOfferId']] = val['id'];
          return acc;
        }, {})
    ).then(setPostMortemScoringParentOfferMetadata);
    fetchPostMortemScoringParentJobMetadata(
      jobs
        .filter(j => j.isPostMortemScoring && !j.parentOfferId)
        .reduce((acc, val) => {
          acc[getPostMortemScoringJobUri(val)] = val['id'];
          return acc;
        }, {})
    ).then(setPostMortemScoringParentJobMetadata);
  }, [jobs]);

  return (
    <TranslatorJobsView
      forReservations={forReservations}
      fetchStatsAndOffers={fetchStatsAndOffers}
      updateStatus={updateStatus}
      jobs={jobs}
      assignedJobsCount={assignedJobsCount}
      maxAssignedJobsCount={maxAssignedJobsCount}
      acceptedWeightedWorkUnits={acceptedWeightedWorkUnits}
      maxAcceptedWeightedWorkUnits={maxAcceptedWeightedWorkUnits}
      isFetching={isFetching || globalState.profile.isFetching}
      isUpdatingOfferMap={isUpdatingOfferMap}
      isUpdatingOfferForClaim={isUpdatingOfferForClaim}
      loadError={loadError}
      inflightStatusUpdates={inflightStatusUpdates}
      alerts={alerts}
      features={features}
      globalState={globalState}
      offerBundlingExpansion={offerBundlingExpansion}
      timeNow={timeNow}
      updateStatusForAll={updateStatusForAll}
      segmentMetadataByJob={segmentMetadataByJob}
      postMortemScoringParentOfferMetadata={postMortemScoringParentOfferMetadata}
      postMortemScoringParentJobMetadata={postMortemScoringParentJobMetadata}
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={pageSize}
      isPaginationLoading={isPaginationLoading}
      onPageChange={handlePageChange}
    />
  );
};

TranslatorJobs.propTypes = {
  forReservations: PropTypes.bool,
  features: PropTypes.object,
  globalState: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default withRouter(TranslatorJobs);
